import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Download, Search } from 'lucide-react';

const GROUP_OPTIONS = [
  { id: 'referrer', label: 'Signup origin (default)' },
  { id: 'business_type', label: 'Business type' },
  { id: 'plan', label: 'Plan' },
  { id: 'none', label: 'No grouping' },
];

const PLAN_OPTIONS = [
  { id: 'all', label: 'All plans' },
  { id: 'memorial', label: 'Memorial ($199)' },
  { id: 'memorial_plus', label: 'Memorial Plus ($299)' },
  { id: 'legacy', label: 'Legacy (internal)' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
}

function ChannelPill({ channel, label }) {
  const map = {
    direct: { bg: '#E8E2D0', fg: '#5C5345' },
    regional_partner: { bg: 'rgba(217,183,117,0.25)', fg: '#8B6A3E' },
    memorial_partner: { bg: 'rgba(164,195,154,0.28)', fg: '#3F6B45' },
    other: { bg: '#E8E2D0', fg: '#5C5345' },
  };
  const c = map[channel] || map.other;
  return (
    <span
      style={{
        backgroundColor: c.bg, color: c.fg,
        padding: '3px 8px', borderRadius: 999,
        fontSize: 11, fontWeight: 600,
      }}
      title={label}
    >
      {channel === 'direct' ? 'Direct' :
        channel === 'regional_partner' ? 'Regional' :
          channel === 'memorial_partner' ? 'Memorial' : channel}
    </span>
  );
}

function toCsv(rows) {
  const headers = [
    'name', 'email', 'phone', 'plan', 'created_at',
    'channel', 'referrer_name', 'referrer_account_type', 'referrer_business_type',
    'regional_partner_above_name',
    'total_paid_usd', 'payment_count', 'last_payment_at', 'last_payment_status',
    'user_id', 'referred_by_partner_id',
  ];
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[,"\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => escape(r[h])).join(','));
  }
  return lines.join('\n');
}

export default function AdminFamilies() {
  const [groupBy, setGroupBy] = useState('referrer');
  const [plan, setPlan] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(h);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get('/admin/families', {
        params: { group_by: groupBy, plan, search: debounced },
      });
      setData(payload);
    } catch (e) {
      toast.error(formatApiError(e) || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  }, [groupBy, plan, debounced]);

  useEffect(() => { load(); }, [load]);

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = [...data.rows];
    rows.sort((a, b) => {
      const av = a[sortBy] ?? '';
      const bv = b[sortBy] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [data, sortBy, sortDir]);

  const grouped = useMemo(() => {
    if (groupBy === 'none' || !sortedRows.length) {
      return [{ key: 'all', label: 'All families', rows: sortedRows }];
    }
    const map = new Map();
    for (const r of sortedRows) {
      const key = r.group_label || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([label, rows]) => ({ key: label, label, rows }));
  }, [sortedRows, groupBy]);

  const onExportCsv = () => {
    const csv = toCsv(sortedRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `myleaf-families-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    toast.success(`Exported ${sortedRows.length} families`);
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  return (
    <div data-testid="admin-families-page" className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2"
                data-testid="admin-families-title">
              Family accounts
            </h1>
            <p className="text-[hsl(var(--primary)/0.7)] text-sm mt-2">
              {data ? `${data.total_families} families · $${data.total_revenue_usd.toLocaleString()} total revenue` : 'Loading…'}
            </p>
          </div>
          <button
            onClick={onExportCsv}
            data-testid="admin-families-export-csv"
            className="ml-btn-outline inline-flex items-center gap-2 px-4 py-2 text-sm"
          >
            <Download size={14} /> Export CSV
          </button>
        </header>

        {/* Channel summary */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6"
               data-testid="admin-families-channel-summary">
            <Stat label="Direct" value={data.channel_counts.direct || 0} />
            <Stat label="Via Regional Partner" value={data.channel_counts.regional_partner || 0} />
            <Stat label="Via Memorial Partner" value={data.channel_counts.memorial_partner || 0} />
            <Stat label="Total revenue (USD)" value={`$${data.total_revenue_usd.toLocaleString()}`} />
          </div>
        )}

        {/* Filters */}
        <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--primary)/0.12)] p-4 mb-4 flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary)/0.6)] mb-1.5">
              Search name / email / phone
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--primary)/0.5)]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Type to filter…"
                data-testid="admin-families-search"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-[hsl(var(--primary)/0.18)] bg-white text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary)/0.6)] mb-1.5">
              Group by
            </label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              data-testid="admin-families-group-select"
              className="px-3 py-2 rounded-md border border-[hsl(var(--primary)/0.18)] bg-white text-sm min-w-[180px]"
            >
              {GROUP_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary)/0.6)] mb-1.5">
              Plan
            </label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              data-testid="admin-families-plan-select"
              className="px-3 py-2 rounded-md border border-[hsl(var(--primary)/0.18)] bg-white text-sm min-w-[140px]"
            >
              {PLAN_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)] p-8 text-center"
               data-testid="admin-families-loading">
            Loading families…
          </div>
        ) : grouped.length === 0 || sortedRows.length === 0 ? (
          <div className="border border-dashed border-[hsl(var(--primary)/0.2)] rounded-lg p-8 text-center text-[hsl(var(--primary)/0.6)]"
               data-testid="admin-families-empty">
            No families match the current filters.
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map((g) => (
              <section key={g.key}
                       data-testid={`admin-families-group-${g.key}`}
                       className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--primary)/0.12)] overflow-hidden">
                <header className="px-4 py-3 bg-[hsl(var(--primary)/0.04)] border-b border-[hsl(var(--primary)/0.08)] flex items-center justify-between">
                  <div className="font-serif text-[15px] text-[hsl(var(--primary))]">
                    {g.label}
                  </div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--primary)/0.6)] font-semibold">
                    {g.rows.length} {g.rows.length === 1 ? 'family' : 'families'}
                  </div>
                </header>
                <div>
                  <table
                    className="w-full text-[12px] table-fixed"
                    data-testid={`admin-families-table-${g.key}`}
                  >
                    <colgroup>
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '11%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '11%' }} />
                      <col style={{ width: '11%' }} />
                      <col style={{ width: '11%' }} />
                    </colgroup>
                    <thead className="bg-[hsl(var(--primary))] text-[hsl(var(--accent))]">
                      <tr>
                        <Th onClick={() => toggleSort('created_at')}
                            active={sortBy === 'created_at'} dir={sortDir}>
                          Signed up
                        </Th>
                        <Th onClick={() => toggleSort('name')}
                            active={sortBy === 'name'} dir={sortDir}>
                          Family
                        </Th>
                        <Th>Plan</Th>
                        <Th onClick={() => toggleSort('referrer_name')}
                            active={sortBy === 'referrer_name'} dir={sortDir}>
                          Referrer
                        </Th>
                        <Th onClick={() => toggleSort('total_paid_usd')}
                            active={sortBy === 'total_paid_usd'} dir={sortDir}
                            align="right">
                          Paid
                        </Th>
                        <Th>Last payment</Th>
                        <Th>Channel</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((r) => (
                        <tr key={r.user_id}
                            data-testid={`admin-families-row-${r.user_id}`}
                            className="border-t border-[hsl(var(--primary)/0.08)] hover:bg-[hsl(var(--primary)/0.02)]">
                          <td className="px-2 py-2 text-[hsl(var(--primary)/0.7)] whitespace-nowrap">
                            {fmtDate(r.created_at)}
                          </td>
                          <td className="px-2 py-2 min-w-0">
                            <div className="font-medium text-[hsl(var(--primary))] truncate" title={r.name || ''}>
                              {r.name || '—'}
                            </div>
                            <div className="text-[10px] text-[hsl(var(--primary)/0.6)] truncate" title={r.email || ''}>
                              <a href={`mailto:${r.email}`} className="hover:underline">
                                {r.email}
                              </a>
                            </div>
                            {r.phone && (
                              <div className="text-[10px] text-[hsl(var(--primary)/0.5)] truncate">{r.phone}</div>
                            )}
                          </td>
                          <td className="px-2 py-2 capitalize text-[hsl(var(--primary)/0.8)] truncate">
                            {r.plan || '—'}
                          </td>
                          <td className="px-2 py-2 min-w-0">
                            {r.referrer_name ? (
                              <>
                                <div className="text-[hsl(var(--primary))] text-[12px] truncate" title={r.referrer_name}>
                                  {r.referrer_name}
                                </div>
                                <div className="text-[10px] text-[hsl(var(--primary)/0.55)] uppercase tracking-wide truncate">
                                  {r.referrer_account_type === 'regional_partner' ? 'Regional' :
                                    r.referrer_account_type === 'memorial_partner' ? 'Memorial' : '—'}
                                  {r.regional_partner_above_name ? ` · ${r.regional_partner_above_name}` : ''}
                                </div>
                              </>
                            ) : <span className="text-[hsl(var(--primary)/0.5)]">—</span>}
                          </td>
                          <td className="px-2 py-2 text-right font-medium text-[hsl(var(--primary))] whitespace-nowrap">
                            ${r.total_paid_usd.toLocaleString()}
                          </td>
                          <td className="px-2 py-2 text-[hsl(var(--primary)/0.7)]">
                            {r.last_payment_at ? (
                              <>
                                <div className="whitespace-nowrap">{fmtDate(r.last_payment_at)}</div>
                                <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--primary)/0.5)] truncate">
                                  {r.last_payment_status}
                                </div>
                              </>
                            ) : '—'}
                          </td>
                          <td className="px-2 py-2">
                            <ChannelPill channel={r.channel} label={r.channel_label} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}
      </AdminLayout>
    </div>
  );
}

function Th({ children, onClick, active, dir, align = 'left' }) {
  return (
    <th
      onClick={onClick}
      className={`px-2 py-2 font-semibold text-[10px] tracking-[0.15em] uppercase whitespace-nowrap text-${align} ${onClick ? 'cursor-pointer hover:opacity-80' : ''}`}
    >
      {children}
      {active && <span className="ml-1 opacity-70">{dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary)/0.6)]">{label}</div>
      <div className="font-serif text-2xl text-[hsl(var(--primary))]">{value ?? 0}</div>
    </div>
  );
}
